import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { fmtCAD, todayISO } from "@/lib/format";
import { Plus, ShieldCheck, Info } from "@phosphor-icons/react";

/**
 * PremiumFormDialog — dedicated entry type for insurance premiums.
 *
 * CRA Line 33099 explicitly allows premiums paid to a "private health
 * services plan" (Blue Cross, Sun Life, Manulife workplace plans, expat
 * health insurance, etc.) as a medical expense. Before this dialog, users
 * had to remember to log a "Prescription" entry and manually set
 * Category = Premium — most users (the screenshot that triggered this
 * feature being a perfect example) ended up with their premiums silently
 * counted as drug costs, leaving the dashboard's "Insurance Premiums" tile
 * at $0.00.
 *
 * Storage shape (compatible with the existing Trip schema):
 *   entry_type:     "prescription"   (shared collection)
 *   category:       "Premium"        (drives the dashboard tile)
 *   service_kind:   "premium"        (lets the logbook badge it)
 *   drug_pharmacy:  <insurer>        (e.g. "Sun Life")
 *   drug_name:      <plan name>      (e.g. "Health Insurance Premium 2026")
 *   drug_amount:    <annual paid>
 *   notes:          "Coverage period: …"   (when filled)
 *
 * Frequencies (monthly/quarterly/annual) are converted to an annual
 * total at save time so the dashboard math stays simple.
 */
const FREQ_MULTIPLIER = {
  one_time: 1,
  monthly: 12,
  quarterly: 4,
  annual: 1,
};

const FREQ_LABEL = {
  one_time: "One-time / single payment",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

const INSURER_SUGGESTIONS = [
  "Sun Life", "Blue Cross", "Manulife", "Canada Life",
  "Green Shield", "Desjardins", "SSQ", "Pacific Blue Cross",
  "Other / workplace plan",
];

export default function PremiumFormDialog({ initial, trigger }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const inferredFreq = useMemo(() => {
    // When editing an existing premium, default frequency = one_time so
    // we don't double-count: the stored drug_amount IS the annual figure.
    return initial?.id ? "one_time" : "annual";
  }, [initial?.id]);

  const [form, setForm] = useState({
    date: initial?.date || todayISO(),
    insurer: initial?.drug_pharmacy || "",
    plan_name: initial?.drug_name || "",
    amount: initial?.drug_amount ? String(initial.drug_amount) : "",
    frequency: inferredFreq,
    period: "",                 // optional "Coverage period: 2026"
    notes: initial?.notes || "",
  });

  // If the user opens an edit and frequency gets recomputed, sync it once.
  useEffect(() => {
    setForm((f) => ({ ...f, frequency: inferredFreq }));
  }, [inferredFreq]);

  const perPayment = Number(form.amount) || 0;
  const annualTotal = useMemo(() => {
    const mult = FREQ_MULTIPLIER[form.frequency] || 1;
    return Math.round(perPayment * mult * 100) / 100;
  }, [perPayment, form.frequency]);

  const save = useMutation({
    mutationFn: async () => {
      if (!form.insurer.trim()) {
        toast.error("Please enter your insurer (e.g. Sun Life, Blue Cross)");
        throw new Error("validation");
      }
      if (annualTotal <= 0) {
        toast.error("Please enter the amount you paid");
        throw new Error("validation");
      }
      // Compose the notes — preserve any user note, prepend the coverage
      // period if provided, and tag with the original frequency so the
      // user can see what the breakdown was at audit time.
      const parts = [];
      if (form.period.trim()) parts.push(`Coverage period: ${form.period.trim()}`);
      if (form.frequency !== "one_time" && form.frequency !== "annual") {
        parts.push(`Billed ${FREQ_LABEL[form.frequency].toLowerCase()} · $${perPayment.toFixed(2)} × ${FREQ_MULTIPLIER[form.frequency]} = $${annualTotal.toFixed(2)}/yr`);
      }
      if (form.notes.trim()) parts.push(form.notes.trim());
      const composedNotes = parts.join(" · ");

      const body = {
        entry_type: "prescription",
        date: form.date,
        category: "Premium",
        service_kind: "premium",
        drug_name: form.plan_name || `${form.insurer} health plan`,
        drug_pharmacy: form.insurer,
        drug_amount: annualTotal,
        notes: composedNotes,
        // Zero everything else — premiums are purely an amount entry.
        din: "",
        service_total: 0, service_covered: 0,
        one_way_km: 0, round_trip: false,
        parking: 0, tolls: 0, meals: 0, accommodation: 0,
        gas: 0, taxi: 0, rental: 0, other_amount: 0, other_desc: "",
        destination: "", provider: "", purpose: "",
      };
      if (initial?.id) return (await api.patch(`/trips/${initial.id}`, body)).data;
      return (await api.post("/trips", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trips"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
      toast.success(initial?.id ? "Premium updated" : "Insurance premium saved", {
        description: "It's now counted under Insurance Premiums on your dashboard.",
      });
      setOpen(false);
      if (!initial) {
        setForm({
          date: todayISO(), insurer: "", plan_name: "", amount: "",
          frequency: "annual", period: "", notes: "",
        });
      }
    },
    onError: (err) => {
      if (err?.message === "validation") return;
      toast.error("Could not save premium");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            data-testid="add-premium-trigger"
            variant="outline"
            className="rounded-full border-[#2C4C3B]/30 bg-[#2C4C3B]/5 hover:bg-[#2C4C3B]/10 text-[#2C4C3B] h-11"
          >
            <ShieldCheck weight="duotone" size={18} className="mr-2" /> Add insurance premium
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0 rounded-2xl overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b border-[#E3E1D9]">
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck weight="duotone" size={20} className="text-[#2C4C3B]" />
            {initial?.id ? "Edit insurance premium" : "Add insurance premium"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5C6660] mt-1">
            Private health-services-plan premiums — Sun Life, Blue Cross, workplace, expat insurance, etc. CRA Line 33099.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* CRA-eligibility primer — keeps users confident this counts */}
          <div className="rounded-xl border border-[#2C4C3B]/20 bg-[#2C4C3B]/5 p-3 flex gap-3 items-start text-xs leading-relaxed">
            <Info weight="duotone" size={18} className="text-[#2C4C3B] shrink-0 mt-0.5" />
            <div className="text-[#3D4540]">
              Premiums you pay for a private health-services plan are CRA-eligible (Line 33099) — including workplace plans where the premium appears on your T4, dental-only plans, and travel health insurance. Government plans (OHIP, MSP, RAMQ) are <strong>not</strong> eligible.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="premium-date" className="mb-1.5 block">Date paid</Label>
              <Input
                id="premium-date"
                data-testid="premium-date"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="premium-frequency" className="mb-1.5 block">Frequency</Label>
              <select
                id="premium-frequency"
                data-testid="premium-frequency"
                value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-[#E3E1D9] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
              >
                <option value="one_time">One-time / lump sum</option>
                <option value="monthly">Monthly (×12)</option>
                <option value="quarterly">Quarterly (×4)</option>
                <option value="annual">Annual</option>
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="premium-insurer" className="mb-1.5 block">Insurer</Label>
            <Input
              id="premium-insurer"
              data-testid="premium-insurer"
              list="premium-insurer-list"
              value={form.insurer}
              onChange={(e) => setForm({ ...form, insurer: e.target.value })}
              placeholder="Sun Life · Blue Cross · Manulife …"
            />
            <datalist id="premium-insurer-list">
              {INSURER_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>

          <div>
            <Label htmlFor="premium-plan" className="mb-1.5 block">Plan name (optional)</Label>
            <Input
              id="premium-plan"
              data-testid="premium-plan-name"
              value={form.plan_name}
              onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
              placeholder="e.g. Extended Health & Dental — Family"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="premium-amount" className="mb-1.5 block">
                Amount {form.frequency !== "annual" && form.frequency !== "one_time" ? `(per ${form.frequency.replace("ly", "")})` : ""}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660] text-sm">$</span>
                <Input
                  id="premium-amount"
                  data-testid="premium-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  className="pl-7"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="premium-period" className="mb-1.5 block">Coverage period (optional)</Label>
              <Input
                id="premium-period"
                data-testid="premium-period"
                value={form.period}
                onChange={(e) => setForm({ ...form, period: e.target.value })}
                placeholder="2026 · Jan–Dec"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="premium-notes" className="mb-1.5 block">Notes (optional)</Label>
            <Textarea
              id="premium-notes"
              data-testid="premium-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Box 85 on T4 · Family coverage"
            />
          </div>

          {/* Live annual total — makes the monthly→annual math visible
              so the user knows what's going on their tax slip. */}
          <div className="rounded-2xl bg-[#1E2420] text-white p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Annual claim total</div>
              <div className="font-[Manrope] text-2xl font-bold mt-0.5" data-testid="premium-annual-total">{fmtCAD(annualTotal)}</div>
              {form.frequency !== "annual" && form.frequency !== "one_time" && perPayment > 0 && (
                <div className="text-[11px] text-white/60 mt-0.5">
                  {fmtCAD(perPayment)} × {FREQ_MULTIPLIER[form.frequency]} = {fmtCAD(annualTotal)}
                </div>
              )}
            </div>
            <ShieldCheck weight="duotone" size={28} className="text-[#D4A373]" />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-[#E3E1D9] shrink-0 bg-white sm:rounded-b-2xl flex-row gap-2 sm:gap-3">
          <Button
            data-testid="premium-cancel"
            variant="outline"
            onClick={() => setOpen(false)}
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            data-testid="premium-save"
            disabled={!form.date || !form.insurer.trim() || annualTotal <= 0 || save.isPending}
            onClick={() => save.mutate()}
            className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white font-semibold px-6 shadow-md shadow-[#2C4C3B]/20 flex-1 sm:flex-none"
          >
            {save.isPending ? "Saving…" : (initial?.id ? "Save changes" : "Save premium")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
